import { PRODUCT_TOUR_VERSION } from "@chessla/shared";

export const PRODUCT_TOUR_EVENT = "chessla:product-tour:start";

export const PRODUCT_TOUR_PAGE_IDS = [
  "home",
  "games",
  "review",
  "patterns",
  "trends",
  "chats",
  "chat",
  "analyze",
  "membership",
  "profile",
  "help",
  "archive",
  "board",
  "settings",
  "about",
] as const;

export type ProductTourPageId = (typeof PRODUCT_TOUR_PAGE_IDS)[number];

export const fullProductTourRoutes = (gameId: string | null): { page: ProductTourPageId; path: string }[] => [
  { page: "home", path: "/home" },
  { page: "games", path: "/games" },
  { page: "analyze", path: "/analyze" },
  ...(gameId ? [{ page: "review" as const, path: `/games/${encodeURIComponent(gameId)}` }] : []),
  { page: "patterns", path: "/patterns" },
  { page: "trends", path: "/trends" },
  { page: "board", path: "/board" },
  { page: "chats", path: "/chats" },
  { page: "chat", path: "/chat" },
  { page: "archive", path: "/archive" },
  { page: "membership", path: "/subscription" },
  { page: "profile", path: "/profile" },
  { page: "settings", path: "/settings" },
  { page: "help", path: "/help" },
  { page: "about", path: "/about" },
];

export const PRODUCT_TOUR_LAYER_IDS = [
  "games:format",
  "games:clock",
  "games:results",
  "patterns:format",
  "patterns:clock",
  "patterns:results",
  "trends:format",
  "trends:clock",
  "trends:results",
  "review:review",
  "review:nikola",
  "review:moves",
  "review:details",
] as const;

export type ProductTourLayerId = (typeof PRODUCT_TOUR_LAYER_IDS)[number];
export type ProductTourId = ProductTourPageId | ProductTourLayerId;
export const PRODUCT_TOUR_IDS = [...PRODUCT_TOUR_PAGE_IDS, ...PRODUCT_TOUR_LAYER_IDS] as const;

export const isProductTourLayerId = (value: string): value is ProductTourLayerId =>
  PRODUCT_TOUR_LAYER_IDS.includes(value as ProductTourLayerId);

export const productTourPageForPath = (pathname: string): ProductTourPageId | null => {
  if (pathname === "/home") return "home";
  if (pathname === "/archive") return "archive";
  if (pathname === "/board") return "board";
  if (pathname === "/settings") return "settings";
  if (pathname === "/about") return "about";
  if (pathname.startsWith("/games/")) return "review";
  if (pathname === "/games") return "games";
  if (pathname === "/patterns") return "patterns";
  if (pathname === "/trends") return "trends";
  if (pathname === "/chats") return "chats";
  if (pathname === "/chat") return "chat";
  if (pathname === "/analyze") return "analyze";
  if (pathname === "/subscription") return "membership";
  if (pathname === "/profile") return "profile";
  if (pathname === "/help") return "help";
  return null;
};

export const productTourProgressKey = (userId: string): string =>
  `chessla:product-tour:${userId}:v${PRODUCT_TOUR_VERSION}`;

const completedTours = (value: string | null): ProductTourId[] => {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (tour): tour is ProductTourId => typeof tour === "string" && PRODUCT_TOUR_IDS.includes(tour as ProductTourId),
    );
  } catch {
    return [];
  }
};

export const hasCompletedProductTourPage = (value: string | null, page: ProductTourPageId): boolean =>
  hasCompletedProductTour(value, page);

export const addCompletedProductTourPage = (value: string | null, page: ProductTourPageId): string =>
  addCompletedProductTour(value, page);

export const hasCompletedProductTour = (value: string | null, tour: ProductTourId): boolean =>
  completedTours(value).includes(tour);

export const addCompletedProductTour = (value: string | null, tour: ProductTourId): string =>
  JSON.stringify([...new Set([...completedTours(value), tour])]);

export const productTourPageForId = (tour: ProductTourId): ProductTourPageId =>
  tour.split(":", 1)[0] as ProductTourPageId;

export const pendingProductTourLayer = (
  marker: string | null,
  page: ProductTourPageId,
  progress: string | null,
): ProductTourLayerId | null => {
  if (!marker || !isProductTourLayerId(marker)) return null;
  if (productTourPageForId(marker) !== page || hasCompletedProductTour(progress, marker)) return null;
  return marker;
};

export const resolveProductTourTarget = (
  root: string,
  target: string | undefined,
  exists: (selector: string) => boolean,
): string | null => {
  if (!exists(root)) return null;
  if (!target) return root;
  const requested = `${root} ${target}`;
  return exists(requested) ? requested : null;
};

export const haveAllProductToursCompleted = (value: string | null): boolean => {
  const completed = new Set(completedTours(value));
  return PRODUCT_TOUR_IDS.every((tour) => completed.has(tour));
};

export const shouldPersistTourStatus = (status: string): boolean => status === "finished" || status === "skipped";

export const replayProductTour = (): void => {
  window.dispatchEvent(new CustomEvent(PRODUCT_TOUR_EVENT, { detail: { mode: "full" } }));
};
